import type { AppInfo } from '../../api/client'

interface AppBrandingHeaderProps {
  appInfo: AppInfo | null
  /** Text shown below app logo. Defaults to "Sign in to continue to" */
  subtitle?: string
  /** Show u.software branding when no app info. Defaults to true */
  showDefaultBranding?: boolean
  /** Custom title for default branding */
  defaultTitle?: string
}

/**
 * App branding header for auth pages during OAuth flow.
 * Shows the requesting app's logo and name, or u.software branding as fallback.
 */
export function AppBrandingHeader({
  appInfo,
  subtitle = 'Sign in to continue to',
  showDefaultBranding = true,
  defaultTitle,
}: AppBrandingHeaderProps) {
  if (appInfo) {
    return (
      <div className="mb-8 text-center">
        {appInfo.logo_url ? (
          <img
            src={appInfo.logo_url}
            alt={appInfo.name}
            className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-sm"
          />
        ) : (
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[var(--text-primary)] to-[var(--text-secondary)] flex items-center justify-center shadow-sm">
            <span className="text-2xl font-bold text-[var(--bg-base)]">
              {appInfo.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <p className="text-sm text-[var(--text-secondary)]">{subtitle}</p>
        <p className="font-medium text-[var(--text-primary)]">
          {appInfo.name}
        </p>
      </div>
    )
  }

  if (!showDefaultBranding) {
    return null
  }

  // Default u.software branding
  return (
    <div className="mb-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[var(--text-primary)] to-[var(--text-secondary)] flex items-center justify-center shadow-sm">
        <span className="text-2xl font-bold text-[var(--bg-base)]">u</span>
      </div>
      {defaultTitle && (
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          {defaultTitle}
        </h1>
      )}
    </div>
  )
}
